import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  fetchSpots,
  fetchSpotBlocks,
  createSpotBlock,
  cancelSpotBlock,
  fetchAllReservations,
} from '../api';

// Admin Scheduling panel — two jobs on one screen:
//   1. Block off cabins / RV spots (maintenance or events) so members can't book them.
//   2. See every member's bookings, focused on checkout dates, to schedule the
//      cleaning crew.
//
// Both are admin-only (the API enforces role='admin'; this UI only renders for admins).

const REASONS = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
];

function fmtDate(d) {
  if (!d) return '';
  // API returns ISO date/datetime; keep just the calendar day, locale-formatted.
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function AdminScheduling() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();

  const [spots, setSpots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Block form state
  const [form, setForm] = useState({ spotId: '', startDate: '', endDate: '', reason: 'maintenance', note: '' });
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState(null);

  // Bookings view state
  const [includePast, setIncludePast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, b, r] = await Promise.all([
          fetchSpots(instance, account),
          fetchSpotBlocks(instance, account),
          fetchAllReservations(instance, account, { includePast }),
        ]);
        if (cancelled) return;
        if (s.ok) setSpots(s.data.spots || []);
        if (b.ok) setBlocks(b.data.blocks || []);
        if (r.ok) setReservations(r.data.reservations || []);
        if (!s.ok || !b.ok || !r.ok) setError('Some scheduling data failed to load.');
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includePast]); // instance/account are stable; re-fetch only when includePast changes

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [s, b, r] = await Promise.all([
        fetchSpots(instance, account),
        fetchSpotBlocks(instance, account),
        fetchAllReservations(instance, account, { includePast }),
      ]);
      if (s.ok) setSpots(s.data.spots || []);
      if (b.ok) setBlocks(b.data.blocks || []);
      if (r.ok) setReservations(r.data.reservations || []);
      if (!s.ok || !b.ok || !r.ok) setError('Some scheduling data failed to load.');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const submitBlock = async (e) => {
    e.preventDefault();
    setFormMsg(null);
    if (!form.spotId || !form.startDate || !form.endDate) {
      setFormMsg({ type: 'error', text: 'Pick a spot and both dates.' });
      return;
    }
    if (form.endDate <= form.startDate) {
      setFormMsg({ type: 'error', text: 'End date must be after start date.' });
      return;
    }
    setSaving(true);
    try {
      const res = await createSpotBlock(instance, account, {
        spotId: form.spotId,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason,
        note: form.note.trim() || undefined,
      });
      if (res.ok) {
        setFormMsg({ type: 'success', text: 'Spot blocked. Those dates are now unavailable to members.' });
        setForm({ spotId: '', startDate: '', endDate: '', reason: 'maintenance', note: '' });
        await refresh();
      } else {
        setFormMsg({ type: 'error', text: res.data?.message || 'Could not create the block.' });
      }
    } catch (e) {
      setFormMsg({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const removeBlock = async (id) => {
    if (!window.confirm('Clear this block and re-open those dates for booking?')) return;
    const res = await cancelSpotBlock(instance, account, id);
    if (res.ok) setBlocks((bs) => bs.filter((b) => b.id !== id));
    else alert(res.data?.message || 'Could not clear the block.');
  };

  return (
    <div className="card">
      <h2>Scheduling</h2>
      <p className="muted">Block spots for maintenance or events, and see who's checking out when so you can schedule the cleaning crew.</p>
      {error && <p className="error-text">{error}</p>}

      {/* ---- Block a spot -------------------------------------------------- */}
      <section className="sched-section">
        <h3>Block off a cabin or RV spot</h3>
        <form className="sched-form" onSubmit={submitBlock}>
          <div className="form-row">
            <label>
              Spot
              <select value={form.spotId} onChange={(e) => setForm({ ...form, spotId: e.target.value })}>
                <option value="">Select a spot…</option>
                {spots.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.spot_type})</option>
                ))}
              </select>
            </label>
            <label>
              Reason
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>
              From (check-in)
              <input type="date" min={todayISO()} value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            </label>
            <label>
              To (check-out, exclusive)
              <input type="date" min={form.startDate || todayISO()} value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            </label>
          </div>
          <label>
            Note (optional)
            <input type="text" placeholder="e.g. Roof repair, Private wedding" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={500} />
          </label>
          <button type="submit" disabled={saving}>{saving ? 'Blocking…' : 'Block these dates'}</button>
          {formMsg && <span className={formMsg.type === 'error' ? 'error-text' : 'success-text'}>{formMsg.text}</span>}
        </form>

        <h4 className="sched-subhead">Active blocks</h4>
        {loading ? <p className="muted">Loading…</p> : blocks.length === 0 ? (
          <p className="muted">No spots are currently blocked.</p>
        ) : (
          <ul className="sched-list">
            {blocks.map((b) => (
              <li key={b.id} className="sched-item">
                <div>
                  <strong>{b.spot_name}</strong>
                  <span className={`block-tag block-${b.reason}`}>{b.reason}</span>
                  <div className="muted">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</div>
                  {b.note && <div className="sched-note">{b.note}</div>}
                </div>
                <button className="link-danger" onClick={() => removeBlock(b.id)}>Clear</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- Booking schedule (cleaning crew) ----------------------------- */}
      <section className="sched-section">
        <div className="sched-head-row">
          <h3>Member bookings</h3>
          <label className="sched-toggle">
            <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />
            Include past
          </label>
        </div>
        <p className="muted">Sorted by checkout date — plan cleaning crews for the day each stay ends.</p>
        {loading ? <p className="muted">Loading…</p> : reservations.length === 0 ? (
          <p className="muted">No bookings{includePast ? '' : ' upcoming'}.</p>
        ) : (
          <div className="table-wrap">
            <table className="sched-table">
              <thead>
                <tr>
                  <th>Check-out</th><th>Spot</th><th>Member</th><th>Contact</th><th>Check-in</th><th>Nights</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id}>
                    <td className="checkout-cell"><strong>{fmtDate(r.end_date)}</strong></td>
                    <td>{r.spot_name} <span className="muted">({r.spot_type})</span></td>
                    <td>{r.member_name || '—'}</td>
                    <td className="contact-cell">
                      {r.member_email && <div>{r.member_email}</div>}
                      {r.member_phone && <div className="muted">{r.member_phone}</div>}
                    </td>
                    <td>{fmtDate(r.start_date)}</td>
                    <td>{r.nights}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
